import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Inbox, Search, RefreshCw, Mail, CheckCircle2, HelpCircle, XCircle, Loader2, Plug, Settings, FileWarning, ExternalLink, Unplug, Filter, Trash2, Paperclip, Zap, Link2,
} from 'lucide-react';
import { Card, CardContent } from '../../../foundation/altzero_base/base/components/ui/card';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { InboxConfigDrawer } from '../components/inbox/InboxConfigDrawer';
import { EmailPreviewDrawer } from '../components/inbox/EmailPreviewDrawer';
import { SuggestionPreviewDialog } from '../components/inbox/SuggestionPreviewDialog';
import { MailboxModeDialog } from '../components/inbox/MailboxModeDialog';
import { DailyIntakeTable } from '../components/inbox/DailyIntakeTable';
import { SyncProgressCard } from '../components/inbox/SyncProgressCard';
import { InboxDropZone } from '../components/inbox/InboxDropZone';
import { billEmailService } from '../services/billEmailService';
import { effectiveSiteStatus } from '../services/siteService';
import type {
  BillEmailAccount, BillEmailConfig, BulkAssignSkip, EmailSuggestion, InboxEmail, InboxStatus, SyncJob,
} from '../services/billEmailService';
import {
  parseStateOf, hasActiveParse, type ParseState,
} from '../utils/parseState';

function isTrustedSender(senderEmail: string, config: BillEmailConfig | null): boolean {
  if (!config?.sender_domains?.length) return false;
  const domain = senderEmail.split('@')[1]?.toLowerCase();
  return !!domain && config.sender_domains.some((d) => domain === d.toLowerCase() || domain.endsWith(`.${d.toLowerCase()}`));
}

const PAGE_SIZE = 15;
const ALREADY_RUNNING_NOTICE = 'A sync is already running — watching that one.';

const STATUS_TABS: { key: InboxStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unmatched', label: 'Unmatched' },
  { key: 'matched_auto', label: 'Auto-matched' },
  { key: 'matched_manual', label: 'Assigned' },
  { key: 'ignored', label: 'Ignored' },
];

function ParseBadge({ state }: { state: ParseState }) {
  if (state === 'none' || state === 'pending') return null;
  const map: Record<'parsing' | 'stalled' | 'parsed' | 'failed', { label: string; cls: string; icon: React.ElementType }> = {
    parsing: { label: 'Parsing...', cls: 'bg-blue-50 text-blue-600', icon: Loader2 },
    stalled: { label: 'Parse stalled', cls: 'bg-amber-50 text-amber-700', icon: FileWarning },
    parsed: { label: 'Parsed', cls: 'bg-green-50 text-green-700', icon: CheckCircle2 },
    failed: { label: 'Parse failed', cls: 'bg-red-50 text-red-700', icon: FileWarning },
  };
  const { label, cls, icon: Icon } = map[state as 'parsing' | 'stalled' | 'parsed' | 'failed'];
  return (
    <span data-testid="parse-badge" data-parse-state={state} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className={`h-3 w-3 ${state === 'parsing' ? 'animate-spin' : ''}`} /> {label}
    </span>
  );
}

// Job 19: matching an email to a site is only half the answer — whether that
// site is Enrolled decides whether a billing period gets created for it. The
// enrolment is judged as at the invoice's period end (not today), so an invoice
// for a window the site was enrolled for still reads as Enrolled after it is
// cancelled. Falls back to today when the invoice carries no period.
function SiteEnrolmentBadge({ email }: { email: InboxEmail }) {
  if (!email.site) return null;
  const asOf = email.detected_period_to ?? undefined;
  const { status } = effectiveSiteStatus(email.site.bv_extn, asOf);
  const cls =
    status === 'Enrolled' ? 'bg-green-50 text-green-700'
      : status === 'Pending Enrolment' ? 'bg-amber-50 text-amber-700'
        : 'bg-gray-100 text-gray-500';
  return (
    <span data-testid="site-enrolment-badge" data-status={status} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${cls}`}>
      {status}
    </span>
  );
}

/**
 * Job 68: why a parse failed, or why a pending one is being called stalled.
 *
 * `parse_error` has always been recorded by the server and fetched by the list,
 * but nothing rendered it — a failure showed as the word "failed" and no reason,
 * which is what "if failed why?" is asking for. A stalled attachment usually has
 * no error at all (the process died before it could record one), so that case
 * gets an explanation rather than a blank.
 */
function ParseErrorNote({ email }: { email: InboxEmail }) {
  const state = parseStateOf(email);
  if (state !== 'failed' && state !== 'stalled') return null;

  const reason = (email.attachments ?? [])
    .map((a) => a.parse_error)
    .find((m): m is string => !!m && m.trim().length > 0);

  const text = reason
    ?? 'The parse stopped without recording a reason — the server was most likely '
      + 'restarted mid-parse. Retry to run it again.';

  return (
    <p data-testid="parse-error-note" data-parse-state={state} className="text-xs text-amber-700 mt-0.5 break-words" title={text}>
      {state === 'failed' ? 'Parse failed: ' : 'Parse stalled: '}{text}
    </p>
  );
}

// How the bulk-assign confirmation names each reason an email is left alone.
const BULK_ASSIGN_SKIP_TEXT: Record<BulkAssignSkip, string> = {
  no_period: 'no billing period',
  multiple_nmis: 'several NMIs (consolidated)',
  not_enrolled: 'site not Enrolled for the period',
  held: 'held by AI for another reason',
  tender_site: 'tender site',
  no_site: 'site missing',
};

function StatusBadge({ status }: { status: InboxStatus }) {
  const map: Record<InboxStatus, { label: string; cls: string; icon: React.ElementType }> = {
    unmatched: { label: 'Unmatched', cls: 'bg-gray-100 text-gray-600', icon: HelpCircle },
    matched_auto: { label: 'Auto-matched', cls: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
    matched_manual: { label: 'Assigned', cls: 'bg-green-100 text-green-700', icon: CheckCircle2 },
    ignored: { label: 'Ignored', cls: 'bg-gray-100 text-gray-400', icon: XCircle },
  };
  const { label, cls, icon: Icon } = map[status];
  return (
    <span data-testid="status-badge" data-status={status} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export function InboxPage() {
  const [emails, setEmails] = useState<InboxEmail[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // `?status=unmatched` (the By NMI banner's link) opens that tab.
  const [status, setStatus] = useState<InboxStatus | 'all'>(() => {
    const wanted = new URLSearchParams(window.location.search).get('status');
    return STATUS_TABS.find((t) => t.key === wanted)?.key ?? 'all';
  });
  const [search, setSearch] = useState('');            // filters the ingested inbox list (DB)
  const [gmailSearch, setGmailSearch] = useState('');  // live Gmail search (suggestions dropdown)
  const gmailSearchSeq = useRef(0);                    // stale-response guard
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [reindexingAll, setReindexingAll] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [accounts, setAccounts] = useState<BillEmailAccount[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [previewEmail, setPreviewEmail] = useState<InboxEmail | null>(null);
  const [syncRange, setSyncRange] = useState<'7' | '20' | '30' | 'custom'>('20');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState<BillEmailConfig | null>(null);
  const [reconnectingId, setReconnectingId] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [mailboxSettingsFor, setMailboxSettingsFor] = useState<BillEmailAccount | null>(null);
  // Mailbox connection is setup, not day-to-day work — it was taking the top of
  // the page above the invoice list every single visit. Its own tab.
  const [pageTab, setPageTab] = useState<'inbox' | 'mailboxes'>('inbox');
  const [suggestions, setSuggestions] = useState<EmailSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [previewSuggestion, setPreviewSuggestion] = useState<EmailSuggestion | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  // The sync run the bottom-right progress card is watching. Set by "Sync now",
  // or on load when a sync is already running for the org.
  const [syncJobId, setSyncJobId] = useState<string | null>(null);

  // A reload used to drop the card mid-sync, because the job id lived only here.
  // Jobs are stored server-side now: pick the running one back up on load.
  useEffect(() => {
    let mounted = true;
    void billEmailService
      .getActiveSyncJob()
      .then(({ success, data, error: err }) => {
        if (!success) console.warn('[inbox] could not check for a running sync:', err);
        else if (mounted && data) setSyncJobId((current) => current ?? data.id);
      })
      .catch((err: unknown) => console.warn('[inbox] could not check for a running sync:', err));
    return () => { mounted = false; };
  }, []);

  const loadCounts = useCallback(async () => {
    const { success, data } = await billEmailService.getInboxCounts();
    if (success && data) setCounts(data);
  }, []);

  // Core fetch. `silent` skips the page-level loading state so a background
  // parse-poll refresh never turns the rows back into skeletons, and a failed
  // poll never clobbers the current view with an error.
  const fetchInbox = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    const { success, data, total: t, error: err } = await billEmailService.listInbox({
      status: status === 'all' ? undefined : status,
      search: search || undefined,
      page,
      pageSize: PAGE_SIZE,
    });
    if (success) {
      setEmails(data ?? []);
      setTotal(t ?? 0);
      setError(null);
    } else if (!silent) {
      setError(err ?? 'Failed to load inbox');
    }
    if (!silent) setLoading(false);
    loadCounts();
  }, [status, search, page, loadCounts]);

  // Skeleton-showing load — used for first load and explicit filter/search/page/tab changes.
  const load = useCallback(() => fetchInbox(), [fetchInbox]);

  const loadAccounts = useCallback(async () => {
    const { success, data } = await billEmailService.listAccounts();
    if (success) setAccounts(data ?? []);
  }, []);

  const loadConfig = useCallback(async () => {
    const { success, data } = await billEmailService.getConfig();
    if (success) setConfig(data ?? null);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  // Keep the latest fetch in a ref so the poll interval can call it without
  // being torn down and recreated whenever emails/filters change.
  const fetchRef = useRef(fetchInbox);
  useEffect(() => { fetchRef.current = fetchInbox; }, [fetchInbox]);

  // While any attachment is actively parsing, poll so the badge updates without a
  // manual refresh. A SINGLE interval is created once when parsing starts and torn
  // down when it stops (or on unmount) — it depends only on the parsing boolean, so
  // it is not re-armed on every emails change. The poll refreshes silently, so rows
  // never flicker to skeletons and open drawers/dialogs are untouched.
  const anyParsing = hasActiveParse(emails);
  useEffect(() => {
    if (!anyParsing) return;
    const id = setInterval(() => { void fetchRef.current({ silent: true }); }, 4000);
    return () => clearInterval(id);
  }, [anyParsing]);

  // Live Gmail search-as-you-type — its own input, separate from the inbox
  // filter. Debounced, and guarded against out-of-order responses: a slow older
  // request must never overwrite a newer query's results (this was the
  // "sometimes search shows wrong/no results" flakiness).
  useEffect(() => {
    const term = gmailSearch.trim();
    if (term.length < 2) { setSuggestions([]); setShowSuggestions(false); setSuggestLoading(false); return; }
    const seq = ++gmailSearchSeq.current;
    // Clear stale results immediately — otherwise the old query's rows sit there
    // looking like they matched the new term while the real search is in flight.
    setSuggestions([]);
    setSuggestLoading(true);
    setShowSuggestions(true);
    const t = setTimeout(async () => {
      const { success, data } = await billEmailService.searchSuggest(term);
      if (gmailSearchSeq.current !== seq) return; // a newer query superseded this one
      setSuggestLoading(false);
      if (success) setSuggestions(data ?? []);
    }, 500);
    return () => clearTimeout(t);
  }, [gmailSearch]);

  // Clicking a suggestion opens the big preview first — nothing gets pulled into
  // the inbox until the user actually looks at it and clicks "Add to Inbox" there.
  // Job 62: an already-ingested suggestion used to return here, which left no
  // way forward. Gmail indexes the text inside PDF attachments, so it finds an
  // email by an NMI that our own inbox search cannot — our search_text is
  // written once at ingest and may predate PDF text extraction. Refusing to
  // open the preview meant the row was reported "in inbox", could not be found
  // in the inbox, and could not be re-added. Adding it again now re-indexes it.
  const handleSelectSuggestion = (s: EmailSuggestion) => {
    setShowSuggestions(false);
    setPreviewSuggestion(s);
  };

  // The email is in. Now show it: drop the term the user searched Gmail with
  // (their NMI) into the inbox filter and reset the view to All, so the row they
  // just added lands in front of them without a second search. Only that one
  // email was added — anything else matching the NMI still needs a sync.
  const handleSuggestionAdded = () => {
    const term = gmailSearch.trim();
    if (term) {
      setSearch(term);
      setStatus('all');
      setPage(1);
    }
    setGmailSearch('');
    setSuggestions([]);
    setShowSuggestions(false);
    setNotice('Pulled into inbox — assign it below.');
    load();
  };

  const handleDelete = async (emailId: string) => {
    if (!window.confirm('Delete this email from the inbox? This only removes our copy — the original stays in Gmail.')) return;
    setDeletingId(emailId);
    await billEmailService.deleteEmail(emailId);
    await load();
    setDeletingId(null);
  };

  // Starts a job and hands it to the progress card — the sync itself can run for
  // minutes, so nothing waits on it here.
  const handleSync = useCallback(async (range?: { days?: number; from?: string; to?: string }) => {
    setSyncing(true);
    const { success, data, error: err } = await billEmailService.syncNow(range);
    setSyncing(false);
    if (!success) {
      setError(err ?? 'Sync failed');
      return;
    }
    if (!data?.job_id) {
      setNotice('No mailbox is set up to collect invoices — connect one under Email connection.');
      return;
    }
    // Only while it is true: a run that started fresh replaces a stale banner.
    if (data.already_running) setNotice(ALREADY_RUNNING_NOTICE);
    else setNotice((current) => (current === ALREADY_RUNNING_NOTICE ? null : current));
    setSyncJobId(data.job_id);
  }, []);

  /**
   * Re-read the backlog with AI.
   *
   * Only emails the AI has never read, so pressing it twice costs nothing. Like
   * the sync, it returns as soon as the work is queued — a few hundred invoices
   * takes minutes, and the rows update as each one finishes.
   */
  const handleReindexAll = useCallback(async () => {
    setReindexingAll(true);
    const { success, data, error: err } = await billEmailService.reindexAll();
    setReindexingAll(false);

    if (!success) {
      setError(err ?? 'Could not start re-reading the inbox');
      return;
    }
    const queued = data?.reindexing ?? 0;
    if (queued === 0) {
      setNotice('Every invoice in the inbox has already been read by AI — nothing to re-read.');
      return;
    }
    // Say when there is more left than one run can take, so a part-done backlog
    // does not look like a finished one.
    const more = data?.maxPerRun && queued >= data.maxPerRun
      ? ` That is the most one run takes — press it again when these finish to continue.`
      : '';
    setNotice(`Re-reading ${queued} invoice${queued === 1 ? '' : 's'} with AI. Meter numbers and periods update as each one finishes.${more}`);
  }, []);

  /**
   * Attach every SAFE auto-matched email to its matched site's bill period.
   *
   * Previewed first, so the confirmation says exactly how many go and how many
   * stay behind and why. The server assigns a batch per call; this keeps calling
   * while there is more, sending back the emails it could not assign so they do
   * not head every later batch. Parsing is not waited on — the server's parse
   * sweep works through the new bills a few at a time.
   */
  const handleBulkAssign = useCallback(async () => {
    setBulkAssigning(true);
    try {
      const preview = await billEmailService.previewBulkAssign();
      if (!preview.success || !preview.data) {
        setError(preview.error ?? 'Could not check the auto-matched emails');
        return;
      }
      const { assignable, skipped } = preview.data;
      const left = (Object.entries(skipped) as [BulkAssignSkip, number][])
        .filter(([, n]) => n > 0)
        .map(([reason, n]) => `  • ${n} — ${BULK_ASSIGN_SKIP_TEXT[reason]}`);
      const leftText = left.length > 0 ? `\n\nLeft for you to assign by hand:\n${left.join('\n')}` : '';

      if (assignable === 0) {
        const needPerson = Object.values(skipped).reduce((a, b) => a + b, 0);
        setNotice(`No auto-matched email can be assigned automatically.${needPerson > 0 ? ` ${needPerson} need a person.` : ''}`);
        return;
      }
      const confirmed = window.confirm(
        `Assign ${assignable} auto-matched email${assignable === 1 ? '' : 's'} to their site?\n\n`
        + 'Each is attached to its site\'s bill period (matched or created) and parsed in the background, '
        + 'a few at a time — a large backlog takes a day or more to finish parsing. '
        + `Bills then go to the bill run once fully parsed.${leftText}`,
      );
      if (!confirmed) return;

      let assigned = 0;
      let failed = 0;
      let periodTaken = 0;
      const setAside: string[] = [];
      for (;;) {
        const { success, data, error: err } = await billEmailService.bulkAssign(setAside);
        if (!success || !data) {
          setError(err ?? 'Bulk assign stopped');
          break;
        }
        assigned += data.assigned;
        failed += data.failed;
        periodTaken += data.periodTaken;
        setAside.push(...data.leftIds);
        setNotice(`Assigning auto-matched emails… ${assigned} of ${assignable} done.`);
        // Every call either assigns or sets aside what it reached, so this ends;
        // the second test only guards against a server that reports neither.
        if (data.remaining === 0 || (data.assigned === 0 && data.leftIds.length === 0)) break;
      }
      const takenNote = periodTaken > 0
        ? ` ${periodTaken} matched a period that already has an invoice and were left for you to check.`
        : '';
      const failedNote = failed > 0 ? ` ${failed} could not be assigned and are still auto-matched.` : '';
      setNotice(`Assigned ${assigned} email${assigned === 1 ? '' : 's'} to their site. Parsing runs in the background.${takenNote}${failedNote}`);
    } finally {
      setBulkAssigning(false);
      void fetchInbox({ silent: true });
    }
  }, [fetchInbox]);

  // A finished sync means new rows may exist. Refresh silently so the current
  // filter (often the NMI just searched) and any open drawer stay put.
  const handleSyncFinished = useCallback((job: SyncJob | null) => {
    void fetchInbox({ silent: true });
    loadConfig();
    // The run it referred to is over; the card says how it ended.
    setNotice((current) => (current === ALREADY_RUNNING_NOTICE ? null : current));
    if (!job) return;
    const filteredNote = job.filtered ? ` (${job.filtered} skipped — attachment wasn't a PDF/Word doc)` : '';
    // Never let the cap read as "we got everything".
    const cappedNote = job.truncated ? ' Older matches were left out — run a wider sync to reach them.' : '';
    setNotice(`Sync complete — ${job.ingested} new email(s) ingested${filteredNote}.${cappedNote}`);
  }, [fetchInbox, loadConfig]);

  // First-time connect leaves the inbox empty until a poll runs — kick one off
  // immediately instead of making the user discover the "Sync now" button.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setNotice('Email account connected — checking for invoice emails...');
      loadAccounts();
      handleSync();
    } else if (params.get('success') === 'false') {
      setNotice(`Connection failed: ${params.get('error') ?? 'unknown error'}`);
    }
    if (params.has('success')) window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    const { success, data, error: err } = await billEmailService.connect('/bill-validation/inbox');
    setConnecting(false);
    if (success && data?.url) window.location.href = data.url;
    else setError(err ?? 'Failed to start connection');
  };

  const handleReconnect = async (accountId: string) => {
    setReconnectingId(accountId);
    const { success, data, error: err } = await billEmailService.connect('/bill-validation/inbox');
    if (success && data?.url) window.location.href = data.url;
    else {
      setReconnectingId(null);
      setError(err ?? 'Failed to start reconnection');
    }
  };

  const handleDisconnect = async (accountId: string) => {
    if (!window.confirm('Disconnect this mailbox from Bill Validation? Existing assigned invoices are unaffected.')) return;
    setDisconnectingId(accountId);
    await billEmailService.disconnectAccount(accountId);
    await loadAccounts();
    setDisconnectingId(null);
  };

  // Re-kick a stalled attachment (parse died with the row stuck on 'parsing'). Spins
  // on that row only, then silently refreshes so the badge flips back to 'Parsing...'.
  const handleRetryParse = async (emailId: string) => {
    setRetryingId(emailId);
    await billEmailService.retryParse(emailId);
    await fetchInbox({ silent: true });
    setRetryingId(null);
  };

  const handleIgnore = async (emailId: string) => {
    await billEmailService.ignore(emailId);
    load();
  };

  const handleSyncClick = () => {
    if (syncRange === 'custom') {
      if (!customFrom) return;
      handleSync({ from: customFrom, to: customTo || undefined });
    } else {
      handleSync({ days: Number(syncRange) });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="inbox-page" className="w-full py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-50">
            <Inbox className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Invoice Inbox</h1>
            <p className="text-sm text-gray-500">Emails automatically pulled from connected mailboxes</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {pageTab === 'mailboxes' && (
            <>
              <Button data-testid="inbox-rules-open" variant="outline" onClick={() => setConfigOpen(true)}>
                <Settings className="h-4 w-4 mr-1.5" />
                Inbox rules
              </Button>
              <Button data-testid="inbox-connect-gmail" onClick={handleConnect} disabled={connecting}>
                <Plug className="h-4 w-4 mr-1.5" />
                {connecting ? 'Connecting...' : 'Connect Gmail'}
              </Button>
            </>
          )}
          {pageTab === 'inbox' && (
          <>
          <Select value={syncRange} onValueChange={(v) => setSyncRange(v as typeof syncRange)}>
            <SelectTrigger data-testid="inbox-sync-range" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="20">Last 20 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="custom">Custom range...</SelectItem>
            </SelectContent>
          </Select>
          {syncRange === 'custom' && (
            <>
              <Input data-testid="inbox-sync-from" type="date" className="w-36" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <span className="text-xs text-gray-400">to</span>
              <Input data-testid="inbox-sync-to" type="date" className="w-36" value={customTo} onChange={(e) => setCustomTo(e.target.value)} placeholder="today" />
            </>
          )}
          <Button
            data-testid="inbox-sync-now"
            variant="outline"
            onClick={handleSyncClick}
            // A job is in flight (progress card is showing) — one Gmail pull at a time.
            disabled={syncing || !!syncJobId || accounts.length === 0 || (syncRange === 'custom' && !customFrom)}
          >
            {syncing || syncJobId ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            Sync now
          </Button>
          {/* Emails already in the inbox keep the meter number and period that
              were worked out when they arrived — AI extraction does not reach
              back over them. This is what re-reads that backlog. */}
          <Button
            data-testid="inbox-reindex-all"
            variant="outline"
            onClick={handleReindexAll}
            disabled={reindexingAll || accounts.length === 0}
            title="Re-read invoices that were added before AI extraction, so their meter number and billing period come from the invoice itself"
          >
            {reindexingAll ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
            Re-read with AI
          </Button>
          {status === 'matched_auto' && (
            <Button
              data-testid="inbox-bulk-assign"
              onClick={handleBulkAssign}
              disabled={bulkAssigning || (counts.matched_auto ?? 0) === 0}
              title="Attach every auto-matched email that has a billing period, one NMI and an Enrolled site to its site's bill"
            >
              {bulkAssigning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />}
              Assign all to site
            </Button>
          )}
          </>
          )}
        </div>
      </div>

      {/* Page tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4 w-fit">
        {([
          { key: 'inbox' as const, label: 'Invoice emails' },
          { key: 'mailboxes' as const, label: 'Email connection' },
        ]).map((t) => (
          <button
            key={t.key}
            data-testid={`inbox-tab-${t.key}`}
            data-active={pageTab === t.key}
            onClick={() => setPageTab(t.key)}
            className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
              pageTab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
            {t.key === 'mailboxes' && accounts.length > 0 && (
              <span className="ml-1.5 text-xs text-gray-400">{accounts.length}</span>
            )}
          </button>
        ))}
      </div>

      {notice && (
        <div data-testid="inbox-notice" className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 flex justify-between">
          {notice}
          <button data-testid="inbox-notice-dismiss" onClick={() => setNotice(null)} className="text-blue-500 hover:text-blue-700">&times;</button>
        </div>
      )}

      {pageTab === 'inbox' && <InboxDropZone onUploaded={() => void fetchInbox({ silent: true })} />}

      {pageTab === 'mailboxes' && (
      <Card className="border-0 shadow-sm mb-4">
        <CardContent className="py-4">
          {accounts.length === 0 ? (
            <div data-testid="mailbox-empty" className="flex items-center justify-between">
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <Mail className="h-4 w-4" /> No mailbox connected yet — connect a Gmail account to start receiving invoice emails here.
              </p>
            </div>
          ) : (
            <div data-testid="mailbox-list" className="space-y-2.5">
              {accounts.map((a) => (
                <div key={a.id} data-testid="mailbox-row" data-account-id={a.id} data-polls-inbox={a.poll_inbox !== false} data-active={a.is_active} className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/60 px-3.5 py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0">
                      <Mail className="h-4 w-4 text-gray-500" />
                    </div>
                    <div className="min-w-0">
                      <p data-testid="mailbox-email" className="text-sm font-medium text-gray-900 truncate">{a.email}</p>
                      {/* What this mailbox is for, in words rather than a flag
                          name. "Send only" on its own told nobody what it meant. */}
                      <p className="text-xs mt-0.5">
                        {a.poll_inbox === false ? (
                          <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                            Sends reports · does not collect invoices
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                            Sends reports · collects invoices
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        {a.is_active ? (
                          <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 className="h-3 w-3" /> Active</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-500"><XCircle className="h-3 w-3" /> Needs reconnect</span>
                        )}
                        <span className="text-gray-300">·</span>
                        <span className="text-gray-300">·</span>
                        Connected {new Date(a.created_at).toLocaleDateString('en-AU')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      data-testid="mailbox-settings"
                      size="sm"
                      variant="outline"
                      title="What this mailbox is used for"
                      onClick={() => setMailboxSettingsFor(a)}
                    >
                      <Settings className="h-3.5 w-3.5 mr-1" />
                      Settings
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="mailbox-reconnect"
                      onClick={() => handleReconnect(a.id)}
                      disabled={reconnectingId === a.id}
                    >
                      {reconnectingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                      Reconnect
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="mailbox-disconnect"
                      onClick={() => handleDisconnect(a.id)}
                      disabled={disconnectingId === a.id}
                    >
                      {disconnectingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5 mr-1" />}
                      Disconnect
                    </Button>
                  </div>
                </div>
              ))}

              {config && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400 pt-1">
                  <Filter className="h-3 w-3" />
                  Pulling every email with a PDF/Word attachment — no subject filter
                  {config.sender_domains.length > 0 && ` · trusted: ${config.sender_domains.join(', ')}`}
                  {config.last_polled_at && ` · last checked ${new Date(config.last_polled_at).toLocaleString('en-AU')}`}
                  <button onClick={() => setConfigOpen(true)} className="text-blue-600 hover:underline ml-1">Edit</button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {pageTab === 'inbox' && <DailyIntakeTable onSyncRange={handleSync} />}

      {pageTab === 'inbox' && (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key}
                  data-testid={`inbox-status-tab-${t.key}`}
                  data-active={status === t.key}
                  onClick={() => { setStatus(t.key); setPage(1); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    status === t.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t.label}
                  <span data-testid={`inbox-status-count-${t.key}`} className={`ml-1.5 text-xs ${status === t.key ? 'text-gray-400' : 'text-gray-400'}`}>
                    {counts[t.key] ?? 0}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              {/* Inbox filter — searches only what's already ingested (DB) */}
              <div className="relative w-56">
                <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                <Input
                  data-testid="inbox-filter"
                  placeholder="Filter inbox..."
                  className="pl-8"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
              </div>

              {/* Live Gmail search — its own input, results in a dropdown */}
              <div className="relative w-64">
              <Mail className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
              <Input
                data-testid="inbox-gmail-search"
                placeholder="Search Gmail (live)..."
                className="pl-8 pr-8"
                value={gmailSearch}
                onChange={(e) => setGmailSearch(e.target.value)}
                onFocus={() => { if (suggestions.length > 0 || suggestLoading) setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              />
              {suggestLoading && (
                <Loader2 className="h-4 w-4 text-blue-500 animate-spin absolute right-2.5 top-2.5" />
              )}

              {showSuggestions && (
                <div data-testid="gmail-suggestions" className="absolute right-0 top-full mt-1.5 w-[36rem] max-h-[28rem] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl z-20">
                  <div className="px-4 py-2.5 text-xs text-gray-500 border-b border-gray-100 flex items-center gap-2 bg-gray-50/80 rounded-t-xl">
                    <Mail className="h-3.5 w-3.5" />
                    <span>Live from Gmail — click a result to preview it, then add it to the inbox</span>
                  </div>
                  {suggestLoading ? (
                    <div data-testid="gmail-suggest-loading" className="px-4 py-10 text-center text-sm text-gray-400 flex flex-col items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Searching Gmail for "{gmailSearch.trim()}"...
                    </div>
                  ) : suggestions.length === 0 ? (
                    <p data-testid="gmail-suggest-empty" className="px-4 py-10 text-center text-sm text-gray-400">No matches in Gmail for "{gmailSearch.trim()}".</p>
                  ) : (
                    suggestions.map((s) => {
                      const key = `${s.account_id}:${s.gmail_message_id}`;
                      return (
                        <button
                          key={key}
                          data-testid="gmail-suggestion"
                          data-message-id={s.gmail_message_id}
                          data-already-ingested={!!s.already_ingested}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelectSuggestion(s)}
                          className="w-full text-left px-4 py-3.5 hover:bg-blue-50/60 border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-3 disabled:cursor-default disabled:hover:bg-transparent"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 truncate">{s.subject || '(no subject)'}</p>
                            <p className="text-xs text-gray-500 truncate mt-0.5">
                              {s.sender_name ? `${s.sender_name} · ` : ''}{s.sender_email}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-gray-400 mb-1">{new Date(s.received_at).toLocaleDateString('en-AU')}</p>
                            {/* Job 62: an already-ingested message is still worth
                                opening. Gmail searches inside the PDF, so it can
                                find an email by an NMI our own inbox search cannot
                                — and re-adding it rebuilds that search index. */}
                            {s.already_ingested ? (
                              <span className="text-[10px] text-gray-400 font-medium">In inbox · Preview →</span>
                            ) : (
                              <span className="text-[11px] text-blue-600 font-semibold">Preview →</span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
              </div>
            </div>
          </div>

          {config?.last_polled_at && (
            <p className="text-xs text-gray-400 mb-3">
              Last synced {new Date(config.last_polled_at).toLocaleString('en-AU')}
            </p>
          )}

          {error && <p data-testid="inbox-error" className="text-sm text-red-600 mb-3">{error}</p>}

          {loading ? (
            <div data-testid="inbox-loading" className="space-y-2">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-lg" />)}
            </div>
          ) : emails.length === 0 ? (
            <p data-testid="inbox-empty" className="text-sm text-gray-500 text-center py-10">No emails in this view.</p>
          ) : (
            <div data-testid="inbox-list" className="divide-y divide-gray-100">
              {emails.map((e, i) => (
                <div key={e.id} data-testid="inbox-row" data-email-id={e.id} data-status={e.status} data-parse-state={parseStateOf(e)} className="flex items-center justify-between py-3 gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p data-testid="inbox-row-subject" className="text-sm font-medium text-gray-900 truncate">{e.subject || '(no subject)'}</p>
                      <StatusBadge status={e.status} />
                      <ParseBadge state={parseStateOf(e)} />
                      {/* Results are newest-first, so when searching (e.g. by NMI) across
                          months of recurring invoices, the top hit is the latest one. */}
                      {search.trim() && page === 1 && i === 0 && emails.length > 1 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-medium shrink-0">
                          Latest
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate flex items-center gap-1.5">
                      <span>
                        {e.sender_name ? `${e.sender_name} · ` : ''}{e.sender_email} · {new Date(e.received_at).toLocaleString('en-AU')}
                      </span>
                      {isTrustedSender(e.sender_email, config) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 text-[10px] font-medium shrink-0">
                          Trusted
                        </span>
                      )}
                      {(!e.attachments || e.attachments.length === 0) && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 shrink-0">
                          <Paperclip className="h-2.5 w-2.5" /> No attachment
                        </span>
                      )}
                    </p>
                    {e.site && (
                      <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                        Site:{' '}
                        <Link
                          data-testid="inbox-row-site-link"
                          to={`/bill-validation/sites/${e.site.id}`}
                          className="text-blue-600 hover:underline"
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          {e.site.nmi ?? e.site.name ?? e.site.id}
                        </Link>
                        <SiteEnrolmentBadge email={e} />
                        {e.bill && ` · ${new Date(e.bill.bill_from).toLocaleDateString('en-AU')} – ${new Date(e.bill.bill_to).toLocaleDateString('en-AU')}`}
                        {/* No bill yet, but the invoice told us its period — show it so the
                            list says which run the email is about before anyone assigns it. */}
                        {!e.bill && e.detected_period_from && e.detected_period_to && (
                          <span className="text-gray-400">
                            {' · '}Detected: {new Date(e.detected_period_from).toLocaleDateString('en-AU')} – {new Date(e.detected_period_to).toLocaleDateString('en-AU')}
                          </span>
                        )}
                        {e.bill_id && (
                          <Link
                            data-testid="inbox-row-bill-link"
                            to={`/bill-validation/bills/${e.bill_id}`}
                            className="inline-flex items-center gap-0.5 text-blue-600 hover:underline ml-1"
                          >
                            View bill <ExternalLink className="h-3 w-3" />
                          </Link>
                        )}
                      </p>
                    )}
                    {/* Job 68: "if failed why?". parse_error was already stored and
                        fetched but never shown, so a failed parse read as "Parse
                        failed" and nothing else. */}
                    <ParseErrorNote email={e} />
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {parseStateOf(e) === 'stalled' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                        data-testid="inbox-row-retry"
                        onClick={() => handleRetryParse(e.id)}
                        disabled={retryingId === e.id}
                      >
                        {retryingId === e.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                        Retry
                      </Button>
                    )}
                    {e.status !== 'matched_manual' ? (
                      <Button data-testid="inbox-row-view" size="sm" onClick={() => setPreviewEmail(e)}>View & Assign</Button>
                    ) : (
                      <Button data-testid="inbox-row-view" size="sm" variant="outline" onClick={() => setPreviewEmail(e)}>View</Button>
                    )}
                    {e.status !== 'ignored' && e.status !== 'matched_manual' && (
                      <Button data-testid="inbox-row-ignore" size="sm" variant="outline" onClick={() => handleIgnore(e.id)}>Ignore</Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="inbox-row-delete"
                      onClick={() => handleDelete(e.id)}
                      disabled={deletingId === e.id}
                    >
                      {deletingId === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <p data-testid="inbox-page-info" className="text-xs text-gray-500">Page {page} of {totalPages} ({total} total)</p>
              <div className="flex gap-2">
                <Button data-testid="inbox-prev" size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button data-testid="inbox-next" size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      <EmailPreviewDrawer
        open={!!previewEmail}
        onClose={() => setPreviewEmail(null)}
        email={previewEmail}
        onAssigned={load}
      />

      <InboxConfigDrawer open={configOpen} onClose={() => { setConfigOpen(false); loadConfig(); }} />

      <SuggestionPreviewDialog
        open={!!previewSuggestion}
        onClose={() => setPreviewSuggestion(null)}
        suggestion={previewSuggestion}
        onAdded={handleSuggestionAdded}
      />

      <MailboxModeDialog
        account={mailboxSettingsFor}
        onClose={() => setMailboxSettingsFor(null)}
        onSaved={() => void load()}
      />

      <SyncProgressCard
        jobId={syncJobId}
        onFinished={handleSyncFinished}
        onDismiss={() => setSyncJobId(null)}
      />
    </div>
  );
}
